import { useEffect, useRef } from 'react'

import type { ConfigurationFlowId } from 'models/purchaseFlowRevamp'

import { selectFlowComplete, usePfStore } from '../../store'
import { PfRevampEventName, usePfRevampAnalytics } from '../../analytics'

/**
 * Confirms a configuration tab as soon as it is complete while the client is looking at it. Values are
 * stored on every change, so there is nothing to save; confirmation only records that the client has
 * seen the tab, which is why a hidden tab never confirms itself. A tab that is no longer complete is
 * un-confirmed, visible or not, and confirmed again once it is.
 */
export const useAutoConfirmFlow = (cartItemId: string, flowId: ConfigurationFlowId, isEnabled: boolean): void => {
  const trackAnalytics = usePfRevampAnalytics()
  const isComplete = usePfStore(selectFlowComplete(cartItemId, flowId))
  const isConfirmed = usePfStore((state) => state.cartItemUserConfirmed[cartItemId]?.[flowId] === true)
  const hasTracked = useRef(false)

  useEffect(() => {
    // A tab can be built from several flows (a document's power of attorney + property information), so a write
    // to one of them can break the tab while leaving the confirmed flow's own data intact.
    if (isConfirmed && !isComplete) {
      usePfStore.getState().markConfigDirty(cartItemId, flowId)
      return
    }
    if (!isEnabled || !isComplete || isConfirmed) return
    usePfStore.getState().markConfigSaved(cartItemId, flowId)
    if (hasTracked.current) return
    hasTracked.current = true
    trackAnalytics({
      type: PfRevampEventName.PRODUCT_CONFIG_SAVED,
      payload: { cartItemId, flowId },
    })
  }, [isEnabled, isComplete, isConfirmed, cartItemId, flowId, trackAnalytics])
}
